import React, {Component} from 'react';
import {ModalBody, ModalFooter} from 'reactstrap';
import {toast} from 'react-toastify';
import {APIClient} from "../../utils/APIClient";
import {isNetworkError, isOk} from "../../utils/apiResponse";
import {LoadErrorState} from "../elements/LoadErrorState";
import {API_URL} from "../../settings/local_settings";
import {Spinner} from "../Spinner";
import {BusinessUnitPeriodField, BusinessUnitPeriodFieldClass} from "./BusinessUnitPeriodField";
import BudgetValueInput from "../elements/BudgetValueInput";
import {CurrencySelect} from "../elements/CurrencySelect";
import { withTranslation } from 'react-i18next';
import {
    TYPE_ONE_BUDGET_FOR_ALL,
    TYPE_ONE_BUDGET_PER_ADGROUP,
    TYPE_ONE_BUDGET_PER_CAMPAIGN,
    getTypesData
} from "../business_units/BusinessUnitTypes";
import {HELP_BU_CONFIGURATION_TYPES, LinkToArticle} from "../help_center/LinkToArticle";
import BudgetPartialGoalsConfiguration from "../elements/BudgetPartialGoalsConfiguration";
import {BU_PERIOD_PERIODIC, BU_PERIOD_TEMPORARY} from "../../periods";
import {DatetimeHelper} from "../../utils/DatetimeHelper";
import {BudgetHelper, PARTIAL_GOALS_INPUT_MODE_CUMULATIVE, PARTIAL_GOALS_INPUT_MODE_DAILY_RANGE} from "../../utils/BudgetHelper";
import {
    AgencyClientSelect,
    SPECIAL_ITEM_CREATE_AGENCY_CLIENT,
    SPECIAL_ITEM_GO_TO_AGENCY_CLIENTS_ASSIGMENT
} from "../selects/AgencyClientSelect";
import {AddClientModal} from "../agency/AddClientModal";
import CustomSelect from "../elements/CustomSelect";
import {LocalStorage} from "../../utils/LocalStorage";

/**
 * El primer y el último día de un período elegido, o los que había si el período no los define.
 * La usan el cambio de período y los valores iniciales.
 */
function datesOfPeriod(aSelectedPeriod, periodFirstDate, periodLastDate) {
    if(aSelectedPeriod !== null && aSelectedPeriod !== undefined) {
        if(aSelectedPeriod["type"] === BU_PERIOD_TEMPORARY) {
            periodFirstDate = aSelectedPeriod["range_definition"]["from_date"] !== "" ? DatetimeHelper.friendlyDDMMYYYYToDate(aSelectedPeriod["range_definition"]["from_date"], "-") : null;
            periodLastDate = aSelectedPeriod["range_definition"]["to_date"] !== "" ? DatetimeHelper.friendlyDDMMYYYYToDate(aSelectedPeriod["range_definition"]["to_date"], "-") : null;
        } else if(aSelectedPeriod["type"] === BU_PERIOD_PERIODIC) {
            const { firstDate, lastDate } = DatetimeHelper.calculateDatesForMonthlyPeriod(aSelectedPeriod["range_definition"]["first_day"]);
            periodFirstDate = firstDate;
            periodLastDate = lastDate;
        }
    }
    return {periodFirstDate, periodLastDate};
}

/**
 * El aviso de que la línea no se creó.
 *
 * Las validaciones del alta llegan TODAS con el código genérico `ERROR` y el motivo en el texto
 * («Ya existe una línea de seguimiento con el mismo nombre», «período mayor a 6 meses»): resolverlas
 * por código las borraría, así que ese texto se muestra. Se lee de `raw_body`, no de `error_message`,
 * que puede ser el `detail` de DRF o un relleno del propio `APIClient`. Un corte de red, un código
 * tipado o el 404 uniforme del gate van a un texto propio.
 */
export function createErrorMessage(response, t) {
    if (isNetworkError(response)) return t('business_unit.create_error_network');
    const backendMessage = response && response.raw_body && response.raw_body.error_message;
    if (response && response.error_code === 'ERROR' && typeof backendMessage === 'string' && backendMessage) {
        return backendMessage;
    }
    return t('business_unit.create_error');
}

/**
 * `initialValues` (opcional): `{name, budget, currency, period}` ya cargados en otra pantalla. Lo pasa
 * el wizard «Nueva línea» de la 2.1 cuando el usuario sale al editor completo para armar una línea
 * por campaña o por grupo: sin esto tenía que volver a escribir lo del paso 2 (review
 * C-rec-fe-linea-05). Sin la prop, el formulario arranca como siempre.
 */
class AddBusinessUnitFormComponent extends Component {
    constructor(props) {
        super(props);

        const { firstDate, lastDate } = DatetimeHelper.calculateDatesForMonthlyPeriod(1);
        const initial = this.props.initialValues || {};
        const initialPeriod = initial.period || BusinessUnitPeriodFieldClass.defaultSelectedPeriod();
        const initialDates = datesOfPeriod(initial.period, firstDate, lastDate);

        this.state = {
            clientIsSelectable:this.props.clientId === undefined,
            loading: true,
            // null = sin error. Con error, el formulario dice que no cargó en vez de quedarse en el spinner.
            loadError: null,
            creatingBusinessUnit: false,
            channel: 'GOOGLE',
            selectedAccount: null,
            period: initialPeriod,
            budgetValue: initial.budget === undefined ? null : initial.budget,
            budgetCurrency: null, // Del server viene qué currency usar como predeterminada
            canEditCurrency: null,
            businessUnitType: null,
            budgetPartialGoals: [],
            partialGoalsInputMode: PARTIAL_GOALS_INPUT_MODE_CUMULATIVE,
            partialGoalsInputDefinition: null,
            periodFirstDate: initialDates.periodFirstDate,
            periodLastDate: initialDates.periodLastDate,
            clientId: this.props.clientId,
            clientName: this.props.clientName,
            allAgencyClients: null,
            showMissingClientSelectionText: false,
            allowSetFirstDayOnPeriodicBusinessUnits: false
        };

        this.handleSelectedChannelChange = this.handleSelectedChannelChange.bind(this);
        this.handleBusinessUnitTypeChange = this.handleBusinessUnitTypeChange.bind(this);
        this.handlePeriodFieldChange = this.handlePeriodFieldChange.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleAccountChange = this.handleAccountChange.bind(this);
        this.footerButton = this.footerButton.bind(this);
        this.handleSelectedBudgetCurrencyChange = this.handleSelectedBudgetCurrencyChange.bind(this);
        this.handleBudgetValueChange = this.handleBudgetValueChange.bind(this);
        this.handleBudgetPartialGoalsChange = this.handleBudgetPartialGoalsChange.bind(this);
        this.handleAgencyClientChange = this.handleAgencyClientChange.bind(this);
        this.afterSuccessfulAgencyClientCreation = this.afterSuccessfulAgencyClientCreation.bind(this);
    }


    componentDidMount() {
        this.updateCurrency();
    }

    handleAgencyClientChange(agencyClientId, agencyClientName) {
        // Si selecciono "NUEVO CLIENTE"
        if (agencyClientId === SPECIAL_ITEM_CREATE_AGENCY_CLIENT) {
            this.createAgencyClientModal.toggle();
        }

        if (agencyClientId === SPECIAL_ITEM_GO_TO_AGENCY_CLIENTS_ASSIGMENT) {
            this.props.history.push("/user/"+LocalStorage.fetchUserId()+"/module/marketing");
        }

        this.setState({...this.state, clientId: agencyClientId, clientName:agencyClientName, showMissingClientSelectionText: agencyClientId === null});
    }

    handleBudgetPartialGoalsChange(budgetPartialGoals) {
        this.setState({...this.state, budgetPartialGoals: budgetPartialGoals});
    }

    handlePartialGoalsConfigurationChange = (payload) => {
        this.setState({
            ...this.state,
            partialGoalsInputMode: payload.inputMode,
            budgetPartialGoals: payload.budgetPartialGoals || [],
            partialGoalsInputDefinition: payload.inputDefinition || null,
        });
    };

    hasClientInformation() {
        return this.state.clientId !== undefined;
    }

    renderCreateNewAgencyClientModal() {
        return <AddClientModal
            showButton={false}
            ref={instance => { this.createAgencyClientModal = instance;}}
            onClientAdded={this.afterSuccessfulAgencyClientCreation}/>
    }

    afterSuccessfulAgencyClientCreation(newAgencyClientId, newAgencyClientName) {
        const allAgencyClients = this.state.allAgencyClients;
        const data = {
            'id': newAgencyClientId,
            'name': newAgencyClientName,
            'active': true
        };

        allAgencyClients.push(data);
        this.setState({
            allAgencyClients: allAgencyClients,
            clientId: newAgencyClientId,
            clientName: newAgencyClientName,
        });
    }

    // API

    // La rama de error es «todo lo que no es OK»: con `status === 'ERROR'` un código tipado o un corte
    // de red no entraban en ninguna rama y el spinner quedaba para siempre (card 5qObKTLi).
    updateCurrency() {
        if(this.hasClientInformation()) {
            new APIClient(this.getAgencyClientURL()).call('GET').then((response) => {
                if (!isOk(response)) {
                    this.failLoad(response);
                    return;
                }
                this.setState({
                    loading: false,
                    budgetCurrency: this.initialCurrencyOr(response.data['default_currency'], response.data['can_edit_currency']),
                    canEditCurrency: response.data['can_edit_currency'],
                    allowSetFirstDayOnPeriodicBusinessUnits: response.data['allow_set_first_day_on_periodic_business_units']
                })
            });
        } else {
            new APIClient(this.getAgencyDataURL()).call('GET').then((response) => {
                if (!isOk(response)) {
                    this.failLoad(response);
                    return;
                }
                this.setState({
                    loading: false,
                    budgetCurrency: this.initialCurrencyOr(response.general_data['default_currency'], response.general_data['can_edit_currency']),
                    canEditCurrency: response.general_data['can_edit_currency'],
                    allAgencyClients: response.clients,
                    allowSetFirstDayOnPeriodicBusinessUnits: response.general_data['allow_set_first_day_on_periodic_business_units']
                })
            });
        }
    }

    failLoad(response) {
        this.setState({loading: false, loadError: (response && (response.error_code || response.status)) || 'ERROR'});
    }

    // Reintentar vuelve al spinner: si no, el clic no se ve.
    retryLoad = () => {
        this.setState({loading: true, loadError: null}, () => this.updateCurrency());
    };

    // La moneda inicial sólo vale si la agencia puede elegirla: si no, manda la por defecto.
    initialCurrencyOr(defaultCurrency, canEditCurrency) {
        const initialCurrency = this.props.initialValues && this.props.initialValues.currency;
        return initialCurrency && canEditCurrency ? initialCurrency : defaultCurrency;
    }

    handleSubmit(event) {
        event.preventDefault();

        if(this.state.clientId === null || this.state.clientId === undefined){
            this.setState({showMissingClientSelectionText: true});
            return;
        }

        this.setState({
            creatingBusinessUnit: true
        });

        const payload = {
            'client_id': this.state.clientId,
            // # 202308: Se comenta porque ahora no hace falta seleccionar una cuenta en el primer paso de crear un presupuesto
            // 'ad_account_id': event.target.account.value,
            'name': event.target.businessunitname.value,
            // 'channel': event.target.campaignChannel.value,
            'currency': this.state.budgetCurrency,
            'allocated_budget': this.state.budgetValue,
            'period': this.state.period,
            'type': this.state.businessUnitType,
        };
        if (this.state.partialGoalsInputMode === PARTIAL_GOALS_INPUT_MODE_DAILY_RANGE) {
            const entries = (this.state.partialGoalsInputDefinition && this.state.partialGoalsInputDefinition.entries) || [];
            payload.budget_partial_goals = BudgetHelper.deriveCumulativeFromDailyRange(
                this.state.budgetValue, this.state.periodFirstDate, this.state.periodLastDate, entries,
            );
            payload.budget_partial_goals_input_definition = BudgetHelper.BudgetPartialGoalDailyRangeTranslateToSubmit(entries);
        } else {
            payload.budget_partial_goals = BudgetHelper.BudgetPartialGoalTranslateToSubmit(this.state.budgetPartialGoals);
            payload.budget_partial_goals_input_definition = null;
        }
        new APIClient(this.submitUrl()).call('POST', payload).then((data) => {
            this.setState({
                creatingBusinessUnit: false
            });

            if (!isOk(data)) {
                toast.error(createErrorMessage(data, this.props.t));
                return;
            }
            this.props.afterSuccessfulSubmit(data.data.business_unit_id)
        });
    }

    handleBudgetValueChange(newAmount) {
        this.setState({budgetValue: newAmount})
    }

    handleSelectedChannelChange(event) {
        event.preventDefault();
        this.setState({
            channel: event.target.value,
            selectedAccount: null
        });
    }

    handleBusinessUnitTypeChange(newType) {
        this.setState({
            businessUnitType: newType
        });
    }

    handleSelectedBudgetCurrencyChange(newCurrency) {
        this.setState({
            budgetCurrency: newCurrency
        });
    }

    handlePeriodFieldChange(aSelectedPeriod) {
        /*{
          "type": "TEMPORARY",
          "range_definition": {
            "from_date": "09/01/2025",
            "to_date": "09/01/2025"
          }
        }
        {
          "type": "PERIODIC",
          "range_definition": {
            "first_day": "1"
          }
        }
        */

        const {periodFirstDate, periodLastDate} = datesOfPeriod(aSelectedPeriod, this.state.periodFirstDate, this.state.periodLastDate);
        this.setState({period: aSelectedPeriod, periodFirstDate: periodFirstDate, periodLastDate: periodLastDate});
    }

    handleAccountChange(accountId, accountName) {
        this.setState({
            selectedAccount: accountId
        })
    }

    /**
     * El botón decía «Siguiente» y ya creaba la línea de control.
     *
     * Este submit dispara `POST business-unit/add`: para cuando aparece la ventana de cuentas, la
     * línea EXISTE. Quien cerraba esa segunda ventana creyendo que había abandonado el alta se
     * encontraba después con una línea vacía y sin presupuesto en su listado, sin haber visto nunca
     * un cartel que dijera que la había creado. El paso siguiente sigue existiendo — cambia lo que
     * el botón promete, no el flujo.
     */
    footerButton() {
        if (this.state.creatingBusinessUnit) {
            return <Spinner color="positive small"/>
        } else {
            const { t } = this.props;
            return <input type="submit" value={t('business_unit.create_and_continue')} className="btn -submit"/>
        }
    }

    renderBudgetCurrencyAndAmount() {
        if (this.state.businessUnitType === TYPE_ONE_BUDGET_FOR_ALL)
            return this.renderOneBudgetForAllInputs();
        else if (this.state.businessUnitType === TYPE_ONE_BUDGET_PER_CAMPAIGN || this.state.businessUnitType === TYPE_ONE_BUDGET_PER_ADGROUP)
            return this.renderOneBudgetPerItemInputs();
    }

    renderOneBudgetPerItemInputs() {
        const { t } = this.props;
        if (!this.state.canEditCurrency)
            return;

        return (
            <div className="row g-3">
                <div className="col-12">
                    <label className="form-label">{t('business_unit.budget_currency')}</label>
                    {this.getCurrencySelectInput()}
                </div>
            </div>);
    }

    getCurrencySelectInput() {
        return <BudgetValueInput
            canEditCurrency={true}
            onlyCurrencySelect={true}
            onCurrencyChange={this.handleSelectedBudgetCurrencyChange}
            onAmountChange={null}
            budgetCurrency={this.state.budgetCurrency}
            budgetAmount={null}
        />;
    }

    renderOneBudgetForAllInputs() {
        const { t } = this.props;
        if (!this.state.canEditCurrency) {
            return (<div className="row g-3">
                <div className="col">
                    <label className="form-label">{t('business_unit.currency')}</label>
                    <CurrencySelect onlyAdAccountCurrencyMode={true}/>
                </div>
                <div className="col">
                    <label className="form-label">{t('business_unit.amount')}</label>
                    {this.getBudgetValueInput(false)}
                </div>
                {this.renderPartialGoals()}
            </div>)
        }

        return (<div className="mb-3">
            <div className="mb-0 pr-0">
                <label className="form-label">{t('business_unit.assigned_budget')}</label>
            </div>
            <div className="row g-3">
                <div className="col">
                    {this.getBudgetValueInput(true)}
                </div>
                <div className={"col"}></div>
            </div>
            {this.renderPartialGoals()}
        </div>)
    }

    renderPartialGoals() {
        const hasCumulative = this.state.budgetPartialGoals && this.state.budgetPartialGoals.length > 0;
        const hasDailyRange = this.state.partialGoalsInputMode === PARTIAL_GOALS_INPUT_MODE_DAILY_RANGE
            && this.state.partialGoalsInputDefinition
            && this.state.partialGoalsInputDefinition.entries
            && this.state.partialGoalsInputDefinition.entries.length > 0;
        return <div key="budget-partial-goals" className="row g-3 mb-3">
            <div className="col-md-12">
                <BudgetPartialGoalsConfiguration
                    enabled={hasCumulative || hasDailyRange}
                    budgetPeriodType={this.state.period.type}
                    budgetFromDate={this.state.periodFirstDate}
                    budgetToDate={this.state.periodLastDate}
                    budgetPartialGoals={this.state.budgetPartialGoals}
                    inputDefinition={this.state.partialGoalsInputDefinition}
                    onConfigurationChange={this.handlePartialGoalsConfigurationChange}
                    budgetTotalAmount={this.state.budgetValue}
                    allowDailyRangeMode={true}
                />
            </div>
        </div>;
    }

    getBudgetValueInput(showCurrency) {
        return <BudgetValueInput
            hideCurrency={!showCurrency}
            canEditCurrency={this.state.canEditCurrency}
            onCurrencyChange={this.handleSelectedBudgetCurrencyChange}
            onAmountChange={this.handleBudgetValueChange}
            budgetCurrency={this.state.budgetCurrency}
            budgetAmount={this.state.budgetValue}
        />;
    }

    renderTypeSelect() {
        const { t } = this.props;
        const typesData = getTypesData(t);
        const types = Object.keys(typesData);
        const value = this.state.businessUnitType === null ? '' : this.state.businessUnitType;
        const result = [];
        const options = types.map((a_type) => ({id: a_type, title: typesData[a_type]["Title"], subtitle: typesData[a_type]["Description"]}));
        result.push(<CustomSelect options={options} onSelectOptionChange={this.handleBusinessUnitTypeChange} />);

        if(value === TYPE_ONE_BUDGET_PER_CAMPAIGN)
            result.push(<p className="field-footnote pb-0 mb-0">{t('business_unit.define_campaign_budget_next_step')}</p>);
        if(value === TYPE_ONE_BUDGET_PER_ADGROUP)
            result.push(<p className="field-footnote pb-0 mb-0">{t('business_unit.define_adgroup_budget_next_step')}</p>);
        return result;
    }

    render() {
        const { t } = this.props;
        if (this.state.loadError !== null) {
            return <ModalBody>
                <LoadErrorState title={t('business_unit.load_error')}
                                text={t('business_unit.load_error_explanation')}
                                retryLabel={t('business_unit.load_error_retry')}
                                onRetry={this.retryLoad}/>
            </ModalBody>
        }
        if (this.state.loading === true) {
            return <div className="box-loader">
                <Spinner color="positive"/>
            </div>
        }

        return [
            <form onSubmit={this.handleSubmit}>
                <ModalBody>
                    <div className="modal-section">
                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <label className="form-label">{t('business_unit.client')}</label>
                                {this.renderClientInput()}
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">{t('business_unit.control_line_name')}</label>
                                <input name="businessunitname" type="text" required={true} className={"form-control"}
                                       defaultValue={(this.props.initialValues && this.props.initialValues.name) || undefined}/>
                            </div>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">{t('business_unit.configuration_type')}<LinkToArticle article={HELP_BU_CONFIGURATION_TYPES}/></label>
                            {this.renderTypeSelect()}
                        </div>
                        {this.renderBudgetCurrencyAndAmount()}
                        <div className="mb-3 mt-3">
                            <label className="form-label separator">{t('business_unit.period')}</label>
                            <BusinessUnitPeriodField onChange={this.handlePeriodFieldChange} hidePeriodicRangeDefinition={!this.state.allowSetFirstDayOnPeriodicBusinessUnits}
                                                     initialPeriod={(this.props.initialValues && this.props.initialValues.period) || undefined}/>
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    {/* Qué queda creado al apretar, dicho ANTES. Las cuentas y los objetivos se
                        cargan en el paso siguiente, y saltearlo deja la línea igual de creada — sólo
                        que vacía. Eso se puede decidir, pero no adivinar. */}
                    <p className="field-footnote mb-0 me-auto">{t('business_unit.create_and_continue_hint')}</p>
                    {this.footerButton()}
                </ModalFooter>
            </form>,
            this.renderCreateNewAgencyClientModal()];

    }

    renderClientInput() {
        if(!this.state.clientIsSelectable)
            return <input name="client" type="text" value={this.state.clientName} readOnly={true} className="form-control"/>;

        return <AgencyClientSelect allAgencyClients={this.state.allAgencyClients}
                                   onAgencyClientChange={this.handleAgencyClientChange}
                                   canCreateNewAgencyClient={true}
                                   selectedAgencyClient={this.state.clientId}
                                   showFormValidationTexts={this.state.showMissingClientSelectionText}
        />
    }

    // URLS

    getAgencyClientURL(channel) {
        return `${API_URL}client/${this.state.clientId}/basic-data`
    }

    getAgencyDataURL() {
        return `${API_URL}get-agency-data`
    }

    submitUrl() {
        return API_URL + 'business-unit/add';
    }
}

export const AddBusinessUnitForm = withTranslation()(AddBusinessUnitFormComponent);
